import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
  Search, X, Plus, Pencil, Trash2, Globe, EyeOff, FileDown, ChevronUp, ChevronDown, BookOpen, CircleCheck, Megaphone,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { Button, Input, Select, Card, CardContent, Dialog } from '../../ui';
import {
  getCursos, getAreas, getConvocatorias, deleteCurso, togglePublicado, bulkCursos,
} from '../api';
// proveedores es común: vive en el módulo LAE
import { getProveedores as getProveedoresLae } from '../../laeformacion/api';
import { handleAuthError } from '../../../../funciones/authHelper';
import Paginacion from '../../comunes/Paginacion';

const SEPE_THEME = { '--brand-dark': '21 90% 42%', '--brand-light': '25 95% 53%' };

// Ampliación: estado sustituye a `activo`. Badge + label + orden lógico.
const ESTADO_META = {
  sin_crear:     { label: 'Sin crear',     orden: 0, badge: 'bg-slate-100 text-slate-500 border-slate-200' },
  en_desarrollo: { label: 'En desarrollo', orden: 1, badge: 'bg-amber-50 text-amber-700 border-amber-200' },
  creado:        { label: 'Creado',        orden: 2, badge: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  inactivo:      { label: 'Inactivo',      orden: 3, badge: 'bg-rose-50 text-rose-700 border-rose-200' },
};
const estadoMeta = (e) => ESTADO_META[e] || { label: e || '—', orden: 9, badge: 'bg-slate-100 text-slate-500 border-slate-200' };

// Estados para el dropdown "Cambiar estado" de las acciones masivas.
const ESTADOS_OPC = [
  { value: 'sin_crear', label: 'Sin crear' },
  { value: 'en_desarrollo', label: 'En desarrollo' },
  { value: 'creado', label: 'Creado' },
  { value: 'inactivo', label: 'Inactivo' },
];

// F2: DATEONLY 'YYYY-MM-DD' → 'dd/mm/yyyy' (vacío si null).
const fmtFechaCorta = (s) => (s ? s.split('-').reverse().join('/') : '');

export default function SepeCursos() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [areas, setAreas] = useState([]);
  const [convs, setConvs] = useState([]);
  const [provs, setProvs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Todos los estados del listado (filtros, búsqueda, orden, página) se inicializan
  // desde la query string para preservarlos al volver de editar un curso y para que
  // el URL sea bookmarkable. El deep-link de área/convocatoria (badges) sigue funcionando.
  const [q, setQ] = useState(searchParams.get('q') || '');
  const [fArea, setFArea] = useState(searchParams.get('area') || '');
  const [fConv, setFConv] = useState(searchParams.get('convocatoria') || '');
  const [fProv, setFProv] = useState(searchParams.get('proveedor') || '');
  const [fEstado, setFEstado] = useState(searchParams.get('estado') || '');
  const [fPub, setFPub] = useState(searchParams.get('publicado') || ''); // '1' | '0' | ''

  const [sortCol, setSortCol] = useState(searchParams.get('orderBy') || 'nombre');
  const [sortDir, setSortDir] = useState(searchParams.get('order') === 'desc' ? -1 : 1); // 1=asc, -1=desc
  const [delItem, setDelItem] = useState(null);
  const [page, setPage] = useState(Math.max(1, parseInt(searchParams.get('page'), 10) || 1));
  const [pageSize, setPageSize] = useState(25);

  // — Acciones masivas —
  const [seleccionados, setSeleccionados] = useState(() => new Set()); // Set de ids
  const [expandirSeleccion, setExpandirSeleccion] = useState(false);   // true = todos los filtrados
  const [accionEnCurso, setAccionEnCurso] = useState(false);
  const [dialogAccion, setDialogAccion] = useState(null); // { accion, estado?, area_id?, areaNombre?, cursos:[] }
  const [menu, setMenu] = useState(null);       // 'estado' | 'areas' | null
  const [areaModo, setAreaModo] = useState(null); // 'anadir' | 'quitar' | null (submenú de áreas)
  const selectAllRef = useRef(null);

  const provMap = useMemo(() => Object.fromEntries(provs.map((p) => [p.id, p.nombre])), [provs]);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = {};
      if (fArea) params.area = fArea;
      if (fConv) params.convocatoria = fConv;
      if (fProv) params.proveedor = fProv;
      if (fEstado) params.estado = fEstado;
      if (fPub) params.publicado_web = fPub;
      const { data } = await getCursos(params);
      setItems(data);
    } catch (e) { if (handleAuthError) handleAuthError(e); }
    finally { setLoading(false); }
  };

  useEffect(() => {
    getAreas().then(({ data }) => setAreas(data)).catch(() => {});
    getConvocatorias().then(({ data }) => setConvs(data)).catch(() => {});
    getProveedoresLae().then(({ data }) => setProvs(data)).catch(() => {});
  }, []);

  useEffect(() => { cargar(); /* eslint-disable-next-line */ }, [fArea, fConv, fProv, fEstado, fPub]);

  // Reset a página 1 al cambiar búsqueda, filtros u orden. Se salta la primera
  // ejecución (montaje) para NO pisar la página que venga del URL. También limpia
  // la selección masiva (el conjunto filtrado cambió → selección obsoleta).
  const primeraEjecucion = useRef(true);
  useEffect(() => {
    if (primeraEjecucion.current) { primeraEjecucion.current = false; return; }
    setPage(1);
    setSeleccionados(new Set());
    setExpandirSeleccion(false);
  }, [q, fArea, fConv, fProv, fEstado, fPub, sortCol, sortDir]);

  // Sincroniza el estado del listado con el URL (replace: true → no llena el historial).
  // Valores por defecto (vacíos, página 1, orden nombre+asc) se omiten → URLs limpios.
  useEffect(() => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (fArea) params.area = fArea;
    if (fConv) params.convocatoria = fConv;
    if (fProv) params.proveedor = fProv;
    if (fEstado) params.estado = fEstado;
    if (fPub) params.publicado = fPub; // '1' | '0'
    if (page > 1) params.page = String(page);
    if (!(sortCol === 'nombre' && sortDir === 1)) {
      params.orderBy = sortCol;
      if (sortDir === -1) params.order = 'desc'; // asc se omite (es el default)
    }
    setSearchParams(params, { replace: true });
  }, [q, fArea, fConv, fProv, fEstado, fPub, page, sortCol, sortDir, setSearchParams]);

  const ordenados = useMemo(() => {
    const t = q.trim().toLowerCase();
    let r = t
      ? items.filter((c) => `${c.nombre} ${c.ref || ''} ${c.ref_sepe || ''}`.toLowerCase().includes(t))
      : [...items];
    const val = (c) => {
      switch (sortCol) {
        case 'convocatoria': return c.convocatoria?.nombre || '';
        case 'proveedor': return provMap[c.proveedor_id] || '';
        case 'horas': return c.horas ?? -1;
        case 'estado': return estadoMeta(c.estado).orden;
        case 'entregado_proveedor': return c.entregado_proveedor ? 1 : 0;
        case 'publicado_web': return c.publicado_web ? 1 : 0;
        default: return (c[sortCol] || '').toString().toLowerCase();
      }
    };
    r.sort((a, b) => { const va = val(a), vb = val(b); return va < vb ? -sortDir : va > vb ? sortDir : 0; });
    return r;
  }, [items, q, sortCol, sortDir, provMap]);

  const totalPaginas = Math.max(1, Math.ceil(ordenados.length / pageSize));
  const pageSafe = Math.min(page, totalPaginas);
  const pageItems = useMemo(
    () => ordenados.slice((pageSafe - 1) * pageSize, pageSafe * pageSize),
    [ordenados, pageSafe, pageSize],
  );

  const sortBy = (col) => {
    if (sortCol === col) setSortDir((d) => -d);
    else { setSortCol(col); setSortDir(1); }
  };
  const Arrow = ({ col }) => sortCol !== col ? null : (sortDir === 1 ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />);

  // — Selección masiva: derivados —
  const pageIds = useMemo(() => pageItems.map((c) => c.id), [pageItems]);
  const allPageSelected = pageIds.length > 0 && pageIds.every((id) => seleccionados.has(id));
  const somePageSelected = pageIds.some((id) => seleccionados.has(id));

  // Estado "indeterminate" del checkbox de cabecera (input nativo, vía ref).
  useEffect(() => {
    if (selectAllRef.current) selectAllRef.current.indeterminate = somePageSelected && !allPageSelected;
  }, [somePageSelected, allPageSelected]);

  // Cambiar de página resetea la selección salvo que se haya expandido a "todos los filtrados".
  useEffect(() => {
    if (!expandirSeleccion) setSeleccionados(new Set());
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleUno = (id) => setSeleccionados((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const toggleSelectPagina = () => setSeleccionados((prev) => {
    const n = new Set(prev);
    if (allPageSelected) pageIds.forEach((id) => n.delete(id));
    else pageIds.forEach((id) => n.add(id));
    return n;
  });
  const seleccionarTodosFiltrados = () => {
    setSeleccionados(new Set(ordenados.map((c) => c.id)));
    setExpandirSeleccion(true);
  };
  const limpiarSeleccion = () => { setSeleccionados(new Set()); setExpandirSeleccion(false); };
  const cerrarMenu = () => { setMenu(null); setAreaModo(null); };

  const abrirDialog = (accion, extra = {}) => {
    cerrarMenu();
    const cursosSel = ordenados.filter((c) => seleccionados.has(c.id));
    setDialogAccion({ accion, ...extra, cursos: cursosSel });
  };

  const fraseDialog = (d) => {
    const n = d.cursos.length;
    const s = n !== 1 ? 's' : '';
    switch (d.accion) {
      case 'publicar': return `Vas a publicar en la web ${n} curso${s}.`;
      case 'despublicar': return `Vas a despublicar de la web ${n} curso${s}.`;
      case 'cambiar_estado': return `Vas a cambiar el estado de ${n} curso${s} a "${estadoMeta(d.estado).label}".`;
      case 'añadir_area': return `Vas a añadir el área "${d.areaNombre}" a ${n} curso${s}.`;
      case 'quitar_area': return `Vas a quitar el área "${d.areaNombre}" de ${n} curso${s}.`;
      default: return `Vas a actualizar ${n} curso${s}.`;
    }
  };
  const fraseExito = (d, n) => {
    switch (d.accion) {
      case 'publicar': return `${n} cursos publicados.`;
      case 'despublicar': return `${n} cursos despublicados.`;
      case 'cambiar_estado': return `${n} cursos cambiados a "${estadoMeta(d.estado).label}".`;
      case 'añadir_area': return `Área ${d.areaNombre} añadida a ${n} cursos.`;
      case 'quitar_area': return `Área ${d.areaNombre} quitada de ${n} cursos.`;
      default: return `${n} cursos actualizados.`;
    }
  };

  const ejecutar = async () => {
    if (!dialogAccion) return;
    setAccionEnCurso(true);
    try {
      const payload = { ids: Array.from(seleccionados), accion: dialogAccion.accion };
      if (dialogAccion.estado) payload.estado = dialogAccion.estado;
      if (dialogAccion.area_id) payload.area_id = dialogAccion.area_id;
      const { data } = await bulkCursos(payload);
      const { aplicados = [], fallidos = [] } = data || {};
      const ok = fraseExito(dialogAccion, aplicados.length);
      if (fallidos.length === 0) {
        toast.success(ok);
      } else {
        const nombres = fallidos.map((f) => f.nombre || `#${f.id}`).slice(0, 3).join(', ');
        toast.warning(`${ok} ${fallidos.length} fallaron: ${nombres}${fallidos.length > 3 ? '…' : ''}`);
      }
      limpiarSeleccion();
      setDialogAccion(null);
      await cargar();
    } catch (e) {
      toast.error('Error al aplicar la acción. Intenta de nuevo.');
      if (handleAuthError) { try { handleAuthError(e); } catch (_) { /* ya avisado */ } }
    } finally {
      setAccionEnCurso(false);
    }
  };

  const publicar = async (c) => {
    if (c.estado === 'inactivo') return;
    const next = !c.publicado_web;
    setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, publicado_web: next } : x)));
    try { await togglePublicado(c.id, next); }
    catch (e) {
      setItems((prev) => prev.map((x) => (x.id === c.id ? { ...x, publicado_web: c.publicado_web } : x)));
      if (handleAuthError) handleAuthError(e);
    }
  };

  const exportExcel = () => {
    const rows = ordenados.map((c) => ({
      Curso: c.nombre,
      Ref: c.ref || '',
      'Ref SEPE': c.ref_sepe || '',
      'Áreas': (c.areas || []).map((a) => a.nombre).join(' / '),
      Convocatoria: c.convocatoria?.nombre || '',
      Horas: c.horas ?? '',
      Proveedor: provMap[c.proveedor_id] || '',
      Estado: estadoMeta(c.estado).label,
      Publicado: c.estado === 'inactivo' ? '—' : (c.publicado_web ? 'Publicado' : 'No publicado'),
      'Fecha entrega proveedor': fmtFechaCorta(c.fecha_entrega_proveedor),
      Entregado: c.entregado_proveedor ? 'Sí' : 'No',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Cursos SEPE');
    XLSX.writeFile(wb, 'cursos_sepe.xlsx');
  };

  const confirmarBorrado = async () => {
    try { await deleteCurso(delItem.id); setDelItem(null); await cargar(); }
    catch (e) { if (handleAuthError) handleAuthError(e); }
  };

  const limpiar = () => { setQ(''); setFArea(''); setFConv(''); setFProv(''); setFEstado(''); setFPub(''); };

  const total = items.length;
  const activos = items.filter((c) => c.estado === 'creado').length;
  const publicados = items.filter((c) => c.estado !== 'inactivo' && c.publicado_web).length;

  const Th = ({ col, children, extra = '' }) => (
    <th className={`px-4 py-2.5 cursor-pointer select-none hover:text-slate-700 ${extra}`} onClick={() => sortBy(col)}>
      {children} <Arrow col={col} />
    </th>
  );

  return (
    <div className="bg-slate-50 text-slate-900 px-6 py-5 min-h-full" style={SEPE_THEME}>

      <div className="mb-5 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-slate-900 leading-tight">Cursos</h1>
          <p className="text-sm text-slate-500 mt-1">Cursos SEPE · <span className="font-medium text-slate-700">{total} cursos</span> · {publicados} publicados</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={exportExcel}><FileDown className="w-4 h-4 mr-1.5" /> Exportar a Excel</Button>
          <Button onClick={() => navigate('/formacion/sepe/cursos/nuevo')}><Plus className="w-4 h-4 mr-1.5" /> Crear curso</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: '#fff7ed', color: '#ea580c' }}><BookOpen className="w-5 h-5" /></div>
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Total cursos</div><div className="text-lg font-semibold text-slate-800">{total}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg grid place-items-center bg-emerald-50 text-emerald-600"><CircleCheck className="w-5 h-5" /></div>
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Creados</div><div className="text-lg font-semibold text-slate-800">{activos}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg grid place-items-center bg-emerald-50 text-emerald-600"><Globe className="w-5 h-5" /></div>
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Publicados</div><div className="text-lg font-semibold text-slate-800">{publicados}</div></div>
        </CardContent></Card>
        <Card><CardContent className="p-4 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg grid place-items-center" style={{ background: '#fff7ed', color: '#ea580c' }}><Megaphone className="w-5 h-5" /></div>
          <div><div className="text-[11px] uppercase tracking-wide text-slate-400">Convocatorias</div><div className="text-lg font-semibold text-slate-800">{convs.length}</div></div>
        </CardContent></Card>
      </div>

      {/* Filtros */}
      <Card className="mb-3">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[220px]">
              <Input
                prefix={<Search className="w-4 h-4" />}
                suffix={q ? <button onClick={() => setQ('')}><X className="w-4 h-4" /></button> : null}
                placeholder="Buscar por nombre o referencia…"
                value={q} onChange={(e) => setQ(e.target.value)}
              />
            </div>
            <Select className="w-48" value={fConv} onChange={(e) => setFConv(e.target.value)}>
              <option value="">Todas las convocatorias</option>
              {convs.map((c) => <option key={c.id} value={c.id}>{c.nombre}</option>)}
            </Select>
            <Select className="w-40" value={fArea} onChange={(e) => setFArea(e.target.value)}>
              <option value="">Todas las áreas</option>
              {areas.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}
            </Select>
            <Select className="w-44" value={fProv} onChange={(e) => setFProv(e.target.value)}>
              <option value="">Todos los proveedores</option>
              {provs.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
            </Select>
            <Select className="w-44" value={fEstado} onChange={(e) => setFEstado(e.target.value)}>
              <option value="">Todos los estados</option>
              <option value="sin_crear">Sin crear</option>
              <option value="en_desarrollo">En desarrollo</option>
              <option value="creado">Creado</option>
              <option value="inactivo">Inactivo</option>
            </Select>
            <Select className="w-48" value={fPub} onChange={(e) => setFPub(e.target.value)}>
              <option value="">Publicados y no publicados</option>
              <option value="1">Solo publicados</option>
              <option value="0">Solo no publicados</option>
            </Select>
            <button onClick={limpiar} className="text-slate-400 hover:text-slate-700 text-sm px-2 py-2 flex items-center gap-1"><X className="w-4 h-4" /> Limpiar</button>
          </div>
        </CardContent>
      </Card>

      {/* Barra de acciones masivas */}
      {seleccionados.size > 0 && (
        <Card className="mb-3">
          <CardContent className="p-3">
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm font-medium text-slate-700">
                {seleccionados.size} curso{seleccionados.size !== 1 ? 's' : ''} seleccionado{seleccionados.size !== 1 ? 's' : ''}
              </span>
              {!expandirSeleccion && seleccionados.size < ordenados.length && (
                <button onClick={seleccionarTodosFiltrados} className="text-sm text-primary underline hover:opacity-80">
                  Seleccionar los {ordenados.length} cursos filtrados
                </button>
              )}
              <div className="flex items-center gap-2 ml-auto flex-wrap">
                <Button size="sm" variant="secondary" onClick={() => abrirDialog('publicar')}>Publicar</Button>
                <Button size="sm" variant="secondary" onClick={() => abrirDialog('despublicar')}>Despublicar</Button>

                {/* Cambiar estado ▼ */}
                <div className="relative">
                  <Button size="sm" variant="secondary" onClick={() => { setMenu(menu === 'estado' ? null : 'estado'); setAreaModo(null); }}>
                    Cambiar estado <ChevronDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                  {menu === 'estado' && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={cerrarMenu} />
                      <div className="absolute right-0 z-20 mt-1 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                        {ESTADOS_OPC.map((e) => (
                          <button key={e.value} onClick={() => abrirDialog('cambiar_estado', { estado: e.value })}
                            className="block w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                            {e.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>

                {/* Áreas ▼ (2 niveles) */}
                <div className="relative">
                  <Button size="sm" variant="secondary" onClick={() => { setMenu(menu === 'areas' ? null : 'areas'); setAreaModo(null); }}>
                    Áreas <ChevronDown className="w-3.5 h-3.5 ml-1" />
                  </Button>
                  {menu === 'areas' && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={cerrarMenu} />
                      <div className="absolute right-0 z-20 mt-1 w-60 max-h-72 overflow-auto bg-white border border-slate-200 rounded-lg shadow-lg py-1">
                        {!areaModo ? (
                          <>
                            <button onClick={() => setAreaModo('anadir')} className="flex w-full items-center justify-between px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Añadir área <span className="text-slate-400">→</span></button>
                            <button onClick={() => setAreaModo('quitar')} className="flex w-full items-center justify-between px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Quitar área <span className="text-slate-400">→</span></button>
                          </>
                        ) : (
                          <>
                            <button onClick={() => setAreaModo(null)} className="block w-full text-left px-3 py-1.5 text-xs text-slate-400 hover:bg-slate-50">← {areaModo === 'anadir' ? 'Añadir' : 'Quitar'} área</button>
                            {areas.length === 0 && <div className="px-3 py-1.5 text-sm text-slate-400">No hay áreas.</div>}
                            {areas.map((a) => (
                              <button key={a.id}
                                onClick={() => abrirDialog(areaModo === 'anadir' ? 'añadir_area' : 'quitar_area', { area_id: a.id, areaNombre: a.nombre })}
                                className="block w-full text-left px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
                                {a.nombre}
                              </button>
                            ))}
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>

                <button onClick={limpiarSeleccion} className="text-sm text-slate-500 hover:text-slate-700 px-2">Desmarcar todos</button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tabla */}
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wide text-left">
              <tr>
                <th className="px-3 py-2.5 w-10">
                  <input ref={selectAllRef} type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 cursor-pointer align-middle accent-slate-800"
                    checked={allPageSelected} onChange={toggleSelectPagina}
                    title="Seleccionar los cursos de esta página" />
                </th>
                <Th col="nombre">Curso</Th>
                <Th col="ref" extra="w-28">Ref.</Th>
                <Th col="ref_sepe" extra="w-28">Ref. SEPE</Th>
                <th className="px-4 py-2.5">Áreas</th>
                <Th col="convocatoria" extra="w-44">Convocatoria</Th>
                <Th col="horas" extra="w-16 text-center">Horas</Th>
                <Th col="proveedor" extra="w-36">Proveedor</Th>
                <Th col="entregado_proveedor" extra="w-24">Entregado</Th>
                <Th col="estado" extra="w-32">Estado</Th>
                <Th col="publicado_web" extra="w-32">Publicado</Th>
                <th className="px-4 py-2.5 w-20 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400">Cargando…</td></tr>}
              {!loading && ordenados.length === 0 && (
                <tr><td colSpan={12} className="px-4 py-10 text-center text-slate-400">Sin resultados con esos filtros.</td></tr>
              )}
              {!loading && pageItems.map((c) => (
                <tr key={c.id} className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/formacion/sepe/cursos/${c.id}`)}>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox"
                      className="h-4 w-4 rounded border-slate-300 cursor-pointer align-middle accent-slate-800"
                      checked={seleccionados.has(c.id)} onChange={() => toggleUno(c.id)} />
                  </td>
                  <td className="px-4 py-2.5 font-medium text-slate-800">{c.nombre}</td>
                  <td className="px-4 py-2.5 text-slate-600">{c.ref || '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600">{c.ref_sepe || '—'}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-wrap gap-1">
                      {(c.areas || []).map((a) => <span key={a.id} className="text-[11px] bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded">{a.nombre}</span>)}
                      {(!c.areas || c.areas.length === 0) && <span className="text-slate-300">—</span>}
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    {c.convocatoria
                      ? <span className="text-[11px] px-2 py-0.5 rounded-full border" style={{ background: '#fff7ed', color: '#c2410c', borderColor: '#fed7aa' }}>{c.convocatoria.nombre}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-2.5 text-center text-slate-600">{c.horas ?? '—'}</td>
                  <td className="px-4 py-2.5 text-slate-600">{provMap[c.proveedor_id] || '—'}</td>
                  <td className="px-4 py-2.5">
                    {c.entregado_proveedor
                      ? <span className="text-[11px] px-2 py-0.5 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200">Sí</span>
                      : <span className="text-[11px] px-2 py-0.5 rounded-full border bg-slate-100 text-slate-500 border-slate-200">No</span>}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`inline-block whitespace-nowrap text-[11px] px-2 py-0.5 rounded-full border ${estadoMeta(c.estado).badge}`}>
                      {estadoMeta(c.estado).label}
                    </span>
                  </td>
                  <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                    {c.estado === 'inactivo' ? (
                      <span className="inline-flex items-center text-[11px] px-2 py-1 rounded-full border bg-slate-50 text-slate-300 border-slate-200 cursor-not-allowed" title="Curso inactivo · cambia el estado para publicar">—</span>
                    ) : c.publicado_web ? (
                      <button onClick={() => publicar(c)} className="inline-flex items-center gap-1.5 text-[11px] px-2 py-1 rounded-full border bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100" title="Publicado · click para despublicar"><Globe className="w-3.5 h-3.5" /> Publicado</button>
                    ) : (
                      <button onClick={() => publicar(c)} className="inline-flex items-center text-[11px] px-2 py-1 rounded-full border bg-slate-50 text-slate-400 border-slate-200 hover:bg-slate-100" title="No publicado · click para publicar"><EyeOff className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                    <button className="text-slate-400 hover:text-slate-700 mr-2" title="Editar" onClick={() => navigate(`/formacion/sepe/cursos/${c.id}`)}><Pencil className="w-4 h-4" /></button>
                    <button className="text-slate-400 hover:text-rose-500" title="Eliminar" onClick={() => setDelItem(c)}><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
        <Paginacion
          page={pageSafe}
          pageSize={pageSize}
          total={ordenados.length}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setPageSize(n); setPage(1); }}
        />
      </Card>

      <Dialog
        open={!!delItem}
        onClose={() => setDelItem(null)}
        title="Eliminar curso"
        description={delItem ? `¿Seguro que quieres eliminar "${delItem.nombre}"? Esta acción no se puede deshacer.` : ''}
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDelItem(null)}>Cancelar</Button>
            <Button variant="danger" onClick={confirmarBorrado}>Eliminar</Button>
          </div>
        }
      />

      {/* Diálogo de confirmación de acción masiva */}
      <Dialog
        open={!!dialogAccion}
        onClose={() => { if (!accionEnCurso) setDialogAccion(null); }}
        title="Confirmar acción masiva"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setDialogAccion(null)} disabled={accionEnCurso}>Cancelar</Button>
            <Button onClick={ejecutar} disabled={accionEnCurso}>{accionEnCurso ? 'Aplicando…' : 'Confirmar'}</Button>
          </div>
        }
      >
        {dialogAccion && (
          <div className="text-sm text-slate-700 space-y-3">
            <p>{fraseDialog(dialogAccion)}</p>
            {dialogAccion.accion === 'añadir_area' && (
              <p className="text-slate-500">Los cursos que ya tengan asignada esta área no se modificarán.</p>
            )}
            {dialogAccion.accion === 'quitar_area' && (
              <p className="text-slate-500">Los cursos que no tengan esta área no se modificarán.</p>
            )}
            {dialogAccion.accion === 'publicar' && dialogAccion.cursos.some((c) => c.estado === 'inactivo') && (
              <div className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-lg px-3 py-2">
                Nota: algunos cursos seleccionados están en estado «Inactivo» y no se mostrarán en la web hasta que cambies su estado.
              </div>
            )}
            <p>¿Continuar?</p>
          </div>
        )}
      </Dialog>
    </div>
  );
}
